import classNames from "classnames/bind";
import React, { useMemo } from "react";
import { TextDataDisplay } from "../..//DataDisplay/TextDataDisplay";
import { Card } from "../../Card/Card";
import { Icon } from "../../Icon";
import styles from "./RadioSelectionCard.module.scss";
import { RadioSelectionCardProps } from "./types";
import { Skeleton } from "../..//Skeleton";

const cx = classNames.bind(styles);

const RadioSelectionCard = ({
  name,
  value,
  index,
  option,
  shouldShowRadioIcon = true,
  isLoading,
  isDisabled,
  onChange,
  className,
  style,
  as: asProp,
  ...restProps
}: RadioSelectionCardProps) => {
  const Tag = asProp ?? "div";

  const { icon, title, description, value: optionValue } = option;

  const id = useMemo(() => `${name}-${index}`, [name, index]);

  const isSelected = useMemo(() => {
    if (value === "true" || value === "false") {
      return optionValue === value;
    }
    return optionValue === value;
  }, [value, optionValue]);

  const onClick = () => onChange(optionValue);

  const stateClassNames = {
    "is-loading": isLoading,
    "is-selected": isSelected,
    "is-disabled": isDisabled,
  };

  return (
    <Tag
      className={cx("container", className)}
      onClick={isDisabled || isLoading ? undefined : onClick}
      {...restProps}
    >
      <Card
        className={cx("card-container", "flash-enabled", stateClassNames)}
        tabIndex={0}
        style={style}
      >
        <input
          type="radio"
          name={name}
          id={id}
          className={cx("radio-input")}
          checked={isSelected}
          aria-labelledby={`${id}-label`}
          readOnly
          tabIndex={-1}
        />
        <div
          className={cx("content-container", {
            "should-show-radio-icon": shouldShowRadioIcon,
          })}
        >
          {icon ? (
            <Skeleton isLoading={isLoading}>
              <div className={cx("icon-container")}>
                <Icon
                  variant={icon}
                  size={22}
                  className={cx("icon", stateClassNames)}
                />
              </div>
            </Skeleton>
          ) : null}
          <div
            className={cx("text-container", {
              "vertically-center": !description,
            })}
          >
            <Skeleton
              isLoading={isLoading}
              className={cx("title-skeleton")}
            >
              <TextDataDisplay
                value={title}
                className={cx("title", stateClassNames)}
                clamp={2}
              />
            </Skeleton>
            {description ? (
              <Skeleton isLoading={isLoading}>
                <TextDataDisplay
                  value={description}
                  className={cx("description", stateClassNames)}
                />
              </Skeleton>
            ) : null}
          </div>
          {shouldShowRadioIcon && (
            <div
              className={cx("radio-icon-container", {
                "is-selected": isSelected,
                "vertically-center": !description,
                stateClassNames,
              })}
            >
              <Skeleton isLoading={isLoading}>
                <Icon
                  variant={isSelected ? "check-circle" : "circle"}
                  size={20}
                  weight={isSelected ? "fill" : "regular"}
                  className={cx({
                    "check-circle-icon": isSelected,
                    "circle-icon": !isSelected,
                    ...stateClassNames,
                  })}
                />
              </Skeleton>
            </div>
          )}
        </div>
      </Card>
    </Tag>
  );
};

RadioSelectionCard.displayName = "RadioSelectionCard";

export default RadioSelectionCard;
